import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelBox } from '../../../api/ai.api.types';
import { AlignmentHint, TARGET_FRAME, computeAlignment, computeReadingGate, computeReadingGateScore, computeSnap, rampColor, resolveGuidanceHint } from '../helper/alignmentGuidance';
import { AlignmentEffect, FrameColorMode, FrameLineStyle } from '../helper/trackerSettings';
import AlignmentParticles from './AlignmentParticles';
import AlignmentMetaballs from './AlignmentMetaballs';
import AlignmentCornerLines from './AlignmentCornerLines';

interface AlignmentGuideProps {
    // The currently detected/tracked box that should end up inside the target frame — the meter
    // box in phase 1 (framing/identify), the display box in phase 2 (tariffs/read).
    box: RelBox | null;
    targetFrame?: RelBox;
    // i18n key for the hint shown while nothing is detected yet (phase-specific wording).
    searchHintKey?: string;
    // Optional second line under the main hint — capture progress ("1 von 2 Zählerständen
    // erfasst") while the tariff cycle is running.
    progressText?: string;
    effects?: AlignmentEffect[];
    // How the SOLL frame is styled — debug-switchable variants (see trackerSettings).
    frameColorMode?: FrameColorMode;
    frameLineStyle?: FrameLineStyle;
    // Fired once on reaching the target (snap edge) — the screen plays the snap earcon here so
    // the moment is audible on iOS too, where navigator.vibrate is a no-op.
    onSnap?: () => void;
}

// A new hint must be stable for this long before it replaces the displayed one — the raw hint
// flips at tracking rate (~30/s) around threshold edges and would flicker as text.
const HINT_STABLE_MS = 300;

const HINT_ARROWS: Partial<Record<AlignmentHint, string>> = {
    moveLeft: '←',
    moveRight: '→',
    moveUp: '↑',
    moveDown: '↓',
};

// Frame colors for the non-ramp modes. Neutral keeps the SOLL frame a quiet, always-legible
// white so color stays the IST feedback channel; snap turns everything green (rampColor(1)).
const NEUTRAL_FRAME_COLOR = 'rgba(255, 255, 255, 0.9)';
const CYAN_FRAME_COLOR = 'rgb(34, 211, 238)'; // tailwind cyan-400
const SNAPPED_FRAME_COLOR = 'rgb(74, 222, 128)'; // tailwind green-400 == rampColor(1)

// Alignment coaching: FIXED corner brackets mark where the tracked box should go — the frame
// never moves (a wandering target would destroy the "put it THERE" clarity). Feedback lives in
// the box itself plus the white→green color ramp, a progress bar, and a single directional hint;
// reaching the target makes the brackets glow and vibrates once (no-op where unsupported, e.g.
// iOS Safari). Optional effect layers (particle stream / metaball strands) render behind it.
const AlignmentGuide = ({
    box,
    targetFrame = TARGET_FRAME,
    searchHintKey = 'aiReading.alignment.noMeter',
    progressText,
    effects = ['cornerLines', 'scrim'],
    frameColorMode = 'neutral',
    frameLineStyle = 'solid',
    onSnap,
}: AlignmentGuideProps) => {
    const { t } = useTranslation();
    const alignment = useMemo(() => computeAlignment(box, targetFrame), [box, targetFrame]);

    const [snapped, setSnapped] = useState(false);
    useEffect(() => {
        setSnapped((prev) => computeSnap(box ? alignment.score : 0, prev));
    }, [alignment.score, box]);

    // Reading gate uses the no-"too close"-penalty score (see alignmentGuidance): recognition
    // runs even when the meter overfills the frame. Once it is open, alignment nagging contradicts
    // the "recognized" overlay — so the hint collapses to holdStill (#53).
    const [readingGateOpen, setReadingGateOpen] = useState(false);
    useEffect(() => {
        setReadingGateOpen((prev) => computeReadingGate(box ? computeReadingGateScore(box, targetFrame) : 0, prev));
    }, [box, targetFrame]);
    const effectiveHint = useMemo(() => resolveGuidanceHint(alignment.hint, readingGateOpen), [alignment.hint, readingGateOpen]);

    const [displayedHint, setDisplayedHint] = useState<AlignmentHint>(effectiveHint);
    useEffect(() => {
        if (effectiveHint === displayedHint) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => setDisplayedHint(effectiveHint), HINT_STABLE_MS);
        return () => window.clearTimeout(timeoutId);
    }, [effectiveHint, displayedHint]);

    const frameRef = useRef<HTMLDivElement>(null);
    const onSnapRef = useRef(onSnap);
    onSnapRef.current = onSnap;
    useEffect(() => {
        if (!snapped) {
            return;
        }
        navigator.vibrate?.(80);
        onSnapRef.current?.();
        // One-shot visual pulse of the brackets — the on-screen counterpart of tone/vibration.
        const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
            frameRef.current?.animate?.(
                [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }],
                { duration: 250, easing: 'ease-out' },
            );
        }
    }, [snapped]);

    const color = rampColor(alignment.score);

    const frameColor = snapped
        ? SNAPPED_FRAME_COLOR
        : frameColorMode === 'ramp'
            ? color
            : frameColorMode === 'cyan'
                ? CYAN_FRAME_COLOR
                : NEUTRAL_FRAME_COLOR;

    const bracket = (position: 'tl' | 'tr' | 'bl' | 'br'): React.CSSProperties => ({
        position: 'absolute',
        width: '28px',
        height: '28px',
        borderColor: frameColor,
        borderStyle: frameLineStyle,
        borderWidth: 0,
        transition: 'border-color 0.2s linear, filter 0.2s linear',
        // drop-shadow follows the rendered L-shape of the bracket; box-shadow would outline the
        // full invisible 28x28 square and read as a clipped glow block.
        filter: snapped ? `drop-shadow(0 0 6px ${frameColor})` : 'none',
        ...(position === 'tl' && { left: 0, top: 0, borderLeftWidth: '3px', borderTopWidth: '3px', borderTopLeftRadius: '8px' }),
        ...(position === 'tr' && { right: 0, top: 0, borderRightWidth: '3px', borderTopWidth: '3px', borderTopRightRadius: '8px' }),
        ...(position === 'bl' && { left: 0, bottom: 0, borderLeftWidth: '3px', borderBottomWidth: '3px', borderBottomLeftRadius: '8px' }),
        ...(position === 'br' && { right: 0, bottom: 0, borderRightWidth: '3px', borderBottomWidth: '3px', borderBottomRightRadius: '8px' }),
    });

    const arrow = HINT_ARROWS[displayedHint];
    const hintText = displayedHint === 'noMeter' ? t(searchHintKey) : t(`aiReading.alignment.${displayedHint}`);

    return (
        <div className={'absolute inset-0 pointer-events-none overflow-hidden'} aria-hidden={true}>
            {effects.includes('scrim') && (
                // Scanner-style window: dim everything OUTSIDE the target frame. A huge box-shadow
                // spread on the (rounded) frame rect needs no canvas and no compositing tricks.
                <div
                    data-testid="alignment-scrim"
                    className={'absolute rounded-2xl'}
                    style={{
                        left: `${targetFrame.x * 100}%`,
                        top: `${targetFrame.y * 100}%`,
                        width: `${targetFrame.w * 100}%`,
                        height: `${targetFrame.h * 100}%`,
                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
                    }}
                />
            )}
            {effects.includes('particles') && <AlignmentParticles box={box} targetBox={targetFrame} score={alignment.score} snapped={snapped} />}
            {effects.includes('metaballs') && <AlignmentMetaballs box={box} targetBox={targetFrame} score={alignment.score} />}
            {effects.includes('cornerLines') && (
                <AlignmentCornerLines box={box} targetBox={targetFrame} score={alignment.score} snapped={snapped} />
            )}
            <div
                ref={frameRef}
                data-testid="alignment-frame"
                data-snapped={snapped ? 'true' : 'false'}
                className={'absolute'}
                style={{
                    left: `${targetFrame.x * 100}%`,
                    top: `${targetFrame.y * 100}%`,
                    width: `${targetFrame.w * 100}%`,
                    height: `${targetFrame.h * 100}%`,
                }}
            >
                <div data-testid="alignment-bracket-tl" style={bracket('tl')} />
                <div data-testid="alignment-bracket-tr" style={bracket('tr')} />
                <div data-testid="alignment-bracket-bl" style={bracket('bl')} />
                <div data-testid="alignment-bracket-br" style={bracket('br')} />
            </div>

            {/* THE one on-screen instruction of the camera phase. Deliberately below the frame:
                the top edge is regularly covered by browser permission banners. text-lg keeps it
                readable at arm's length. */}
            <div
                className={'absolute left-0 right-0 flex flex-col items-center gap-1 px-4'}
                style={{ top: `${(targetFrame.y + targetFrame.h) * 100}%`, marginTop: '12px' }}
            >
                <div
                    data-testid="alignment-hint"
                    className={'text-lg font-semibold text-white bg-black bg-opacity-60 rounded-full px-4 py-1.5 text-center max-w-full'}
                    style={displayedHint === 'holdStill' ? { color } : undefined}
                >
                    {arrow && <span className={'mr-1'}>{arrow}</span>}
                    {hintText}
                </div>
                {progressText && (
                    <div
                        data-testid="alignment-progress-text"
                        className={'text-sm font-medium text-white bg-black bg-opacity-60 rounded-full px-3 py-1 text-center max-w-full'}
                    >
                        {progressText}
                    </div>
                )}
                <div className={'w-1/2 h-1 rounded-full bg-white bg-opacity-25 overflow-hidden'}>
                    <div
                        data-testid="alignment-progress"
                        className={'h-full rounded-full'}
                        style={{ width: `${Math.round(alignment.score * 100)}%`, backgroundColor: color, transition: 'width 0.2s linear, background-color 0.2s linear' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default AlignmentGuide;
